import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, KeyRound, LogOut, Mail, MapPin, RefreshCcw, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { STATUSES, STATUS_STYLES, mapsPointLink } from "@/lib/constants";
import { useToast } from "@/hooks/use-toast";
import TestimonialsPanel from "@/components/admin/TestimonialsPanel";
import BannerPanel from "@/components/admin/BannerPanel";
import AdminsPanel from "@/components/admin/AdminsPanel";
import PricingPanel from "@/components/admin/PricingPanel";

function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleString("id-ID", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function AuthCard({ children, testid }) {
    return (
        <div className="grid min-h-screen place-items-center bg-[#EEF3FF] px-4">
            <div
                data-testid={testid}
                className="w-full max-w-md rounded-3xl border border-border bg-white p-10 shadow-brand-lg"
            >
                <img
                    src="/logo.webp"
                    alt="DigiKlin — come do laundry with us"
                    className="mx-auto h-16 w-auto"
                />
                {children}
            </div>
        </div>
    );
}

function LoginForm({ onLogin, onForgot }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const res = await api.post("/admin/login", { email, password });
            onLogin(res.data.token, res.data.admin);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthCard testid="admin-login-card">
            <form data-testid="admin-login-form" onSubmit={submit}>
                <h1 className="mt-6 text-center font-display text-2xl font-extrabold text-ink">
                    Dashboard Admin DigiKlin
                </h1>
                <p className="mt-2 text-center text-sm text-ink/50">
                    Masuk untuk melihat pesanan antar jemput yang masuk.
                </p>

                <div className="mt-8 space-y-5">
                    <div className="space-y-2">
                        <Label htmlFor="admin-email" className="text-sm font-bold text-ink/80">
                            Email
                        </Label>
                        <Input
                            id="admin-email"
                            data-testid="admin-email-input"
                            type="email"
                            required
                            placeholder="admin@digiklin.id"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="admin-password" className="text-sm font-bold text-ink/80">
                            Password
                        </Label>
                        <Input
                            id="admin-password"
                            data-testid="admin-password-input"
                            type="password"
                            required
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>

                    {error && (
                        <p
                            data-testid="admin-login-error"
                            className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600"
                        >
                            {error}
                        </p>
                    )}

                    <Button
                        data-testid="admin-login-button"
                        type="submit"
                        disabled={loading}
                        className="h-12 w-full rounded-full bg-brand text-base font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                    >
                        {loading ? "Memeriksa..." : "Masuk"}
                    </Button>

                    <button
                        type="button"
                        data-testid="admin-forgot-link"
                        onClick={onForgot}
                        className="w-full text-center text-sm font-semibold text-ink/40 transition-colors hover:text-brand"
                    >
                        Lupa password?
                    </button>
                </div>

                <a
                    data-testid="admin-back-to-site"
                    href="/"
                    className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-ink/40 transition-colors hover:text-brand"
                >
                    <ArrowLeft size={15} />
                    Kembali ke website
                </a>
            </form>
        </AuthCard>
    );
}

function ForgotView({ onBack }) {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await api.post("/admin/forgot-password", { email });
            setSent(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthCard testid="admin-forgot-card">
            <form data-testid="admin-forgot-form" onSubmit={submit}>
                <h1 className="mt-6 text-center font-display text-2xl font-extrabold text-ink">
                    Lupa Password
                </h1>
                {sent ? (
                    <div data-testid="forgot-success" className="mt-4">
                        <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold leading-relaxed text-emerald-700">
                            Jika email terdaftar, tautan atur ulang password sudah
                            dikirim. Cek kotak masuk Anda — tautan berlaku 1 jam.
                        </p>
                        <Button
                            type="button"
                            onClick={onBack}
                            variant="outline"
                            className="mt-5 h-12 w-full rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            Kembali ke Login
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="mt-2 text-center text-sm text-ink/50">
                            Masukkan email login Anda. Kami akan mengirim tautan
                            untuk membuat password baru.
                        </p>
                        <div className="mt-8 space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="forgot-email" className="text-sm font-bold text-ink/80">
                                    Email
                                </Label>
                                <Input
                                    id="forgot-email"
                                    data-testid="forgot-email-input"
                                    type="email"
                                    required
                                    placeholder="admin@digiklin.id"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                />
                            </div>
                            {error && (
                                <p
                                    data-testid="forgot-error"
                                    className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600"
                                >
                                    {error}
                                </p>
                            )}
                            <Button
                                data-testid="forgot-submit"
                                type="submit"
                                disabled={loading}
                                className="h-12 w-full rounded-full bg-brand text-base font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                            >
                                {loading ? "Mengirim..." : "Kirim Tautan Reset"}
                            </Button>
                            <Button
                                type="button"
                                onClick={onBack}
                                variant="outline"
                                className="h-12 w-full rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                            >
                                Kembali ke Login
                            </Button>
                        </div>
                    </>
                )}
            </form>
        </AuthCard>
    );
}

function ResetView({ token, onBack }) {
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8 || pw !== confirm) {
            setError("Password minimal 8 karakter dan konfirmasi harus sama.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            await api.post("/admin/reset-password", { token, new_password: pw });
            setDone(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthCard testid="admin-reset-card">
            <form data-testid="admin-reset-form" onSubmit={submit}>
                <h1 className="mt-6 text-center font-display text-2xl font-extrabold text-ink">
                    Buat Password Baru
                </h1>
                {done ? (
                    <div data-testid="reset-success" className="mt-4">
                        <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold leading-relaxed text-emerald-700">
                            Password berhasil diatur ulang. Silakan login dengan
                            password baru Anda.
                        </p>
                        <Button
                            type="button"
                            onClick={onBack}
                            className="mt-5 h-12 w-full rounded-full bg-brand font-bold text-white hover:bg-brand-deep"
                        >
                            Ke Halaman Login
                        </Button>
                    </div>
                ) : (
                    <div className="mt-8 space-y-5">
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                Password Baru <span className="font-medium text-ink/40">(minimal 8 karakter)</span>
                            </Label>
                            <Input
                                data-testid="reset-pw-new"
                                type="password"
                                required
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Konfirmasi Password Baru</Label>
                            <Input
                                data-testid="reset-pw-confirm"
                                type="password"
                                required
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        {error && (
                            <p
                                data-testid="reset-error"
                                className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600"
                            >
                                {error}
                            </p>
                        )}
                        <Button
                            data-testid="reset-submit"
                            type="submit"
                            disabled={loading}
                            className="h-12 w-full rounded-full bg-brand text-base font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                        >
                            {loading ? "Menyimpan..." : "Simpan Password Baru"}
                        </Button>
                        <Button
                            type="button"
                            onClick={onBack}
                            variant="outline"
                            className="h-12 w-full rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            Kembali ke Login
                        </Button>
                    </div>
                )}
            </form>
        </AuthCard>
    );
}

function LoginScreen({ onLogin }) {
    const [view, setView] = useState(() => {
        const t = new URLSearchParams(window.location.search).get("reset");
        return t ? { mode: "reset", token: t } : { mode: "login" };
    });

    const backToLogin = () => {
        window.history.replaceState(null, "", "/admin");
        setView({ mode: "login" });
    };

    if (view.mode === "forgot") return <ForgotView onBack={backToLogin} />;
    if (view.mode === "reset") return <ResetView token={view.token} onBack={backToLogin} />;
    return <LoginForm onLogin={onLogin} onForgot={() => setView({ mode: "forgot" })} />;
}

function PasswordDialog({ me, open, onOpenChange, toast }) {
    const [oldPw, setOldPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (newPw.length < 8 || newPw !== confirm) {
            toast({
                title: "Password tidak valid",
                description: "Minimal 8 karakter dan konfirmasi harus sama.",
                variant: "destructive",
            });
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/admins/${me.id}/password`, {
                old_password: oldPw,
                new_password: newPw,
            });
            toast({ title: "Password Anda berhasil diganti" });
            setOldPw("");
            setNewPw("");
            setConfirm("");
            onOpenChange(false);
        } catch (err) {
            toast({
                title: "Gagal mengganti password",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="self-password-dialog" className="rounded-3xl sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl font-extrabold text-ink">
                        Ganti Password Saya
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm font-bold text-ink/80">Password Lama</Label>
                        <Input
                            data-testid="self-pw-old"
                            type="password"
                            value={oldPw}
                            onChange={(e) => setOldPw(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-bold text-ink/80">
                            Password Baru <span className="font-medium text-ink/40">(minimal 8 karakter)</span>
                        </Label>
                        <Input
                            data-testid="self-pw-new"
                            type="password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-bold text-ink/80">Konfirmasi Password Baru</Label>
                        <Input
                            data-testid="self-pw-confirm"
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>
                </div>
                <div className="flex gap-3 pt-2">
                    <Button
                        data-testid="self-pw-save"
                        onClick={save}
                        disabled={saving}
                        className="h-11 flex-1 rounded-full bg-brand font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                    >
                        {saving ? "Menyimpan..." : "Simpan Password"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                    >
                        Batal
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function EmailDialog({ me, open, onOpenChange, onAuthUpdate, toast }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const newEmail = email.trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail)) {
            toast({
                title: "Email tidak valid",
                description: "Masukkan alamat email yang benar.",
                variant: "destructive",
            });
            return;
        }
        if (!password) {
            toast({
                title: "Password wajib diisi",
                description: "Konfirmasi dengan password Anda saat ini.",
                variant: "destructive",
            });
            return;
        }
        setSaving(true);
        try {
            const res = await api.patch("/admin/me/email", {
                email: newEmail,
                password,
            });
            onAuthUpdate(res.data.token, res.data.admin);
            toast({
                title: "Email login berhasil diganti",
                description: `Email Anda sekarang ${res.data.admin.email}.`,
            });
            setEmail("");
            setPassword("");
            onOpenChange(false);
        } catch (err) {
            toast({
                title: "Gagal mengganti email",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="self-email-dialog" className="rounded-3xl sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl font-extrabold text-ink">
                        Ganti Email Login
                    </DialogTitle>
                    <DialogDescription className="text-sm text-ink/50">
                        Isi email baru dan password Anda saat ini untuk mengganti
                        email login.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm font-bold text-ink/80">Email Baru</Label>
                        <Input
                            data-testid="self-email-new"
                            type="email"
                            placeholder={me.email}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-bold text-ink/80">Password Saat Ini</Label>
                        <Input
                            data-testid="self-email-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                        />
                    </div>
                    <p className="flex items-start gap-2 rounded-xl bg-brand-mist/60 px-4 py-3 text-xs leading-relaxed text-ink/60">
                        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand" />
                        Email baru menjadi email login Anda berikutnya. Sesi Anda saat ini tetap aktif.
                    </p>
                </div>
                <div className="flex gap-3 pt-2">
                    <Button
                        data-testid="self-email-save"
                        onClick={save}
                        disabled={saving}
                        className="h-11 flex-1 rounded-full bg-brand font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                    >
                        {saving ? "Menyimpan..." : "Simpan Email"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                    >
                        Batal
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Dashboard({ me, onLogout, onAuthUpdate }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("orders");
    const [pwOpen, setPwOpen] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const { toast } = useToast();
    const isSuper = me.role === "superadmin";

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/orders");
            setOrders(res.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                onLogout();
                return;
            }
            toast({
                title: "Gagal memuat pesanan",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [onLogout, toast]);

    useEffect(() => {
        load();
        const interval = setInterval(load, 20000);
        return () => clearInterval(interval);
    }, [load]);

    const updateStatus = async (orderId, status) => {
        try {
            const res = await api.patch(`/admin/orders/${orderId}`, { status });
            setOrders((prev) =>
                prev.map((o) => (o.id === orderId ? res.data : o))
            );
            toast({ title: `Status diubah: ${status}` });
        } catch (err) {
            toast({
                title: "Gagal mengubah status",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const stats = useMemo(() => {
        const by = { Baru: 0, Diproses: 0, Selesai: 0 };
        orders.forEach((o) => {
            if (by[o.status] !== undefined) by[o.status] += 1;
        });
        return [
            { label: "Total Pesanan", value: orders.length },
            { label: "Baru", value: by.Baru },
            { label: "Diproses", value: by.Diproses },
            { label: "Selesai", value: by.Selesai },
        ];
    }, [orders]);

    const tabs = [
        { id: "orders", label: "Pesanan" },
        { id: "testimonials", label: "Testimoni" },
        { id: "banners", label: "Banner Promo" },
        ...(isSuper
            ? [
                  { id: "pricing", label: "Harga & Layanan" },
                  { id: "admins", label: "Admin" },
              ]
            : []),
    ];

    return (
        <div className="min-h-screen bg-[#EEF3FF]" data-testid="admin-dashboard">
            <header className="border-b border-border bg-white/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4">
                    <div className="flex items-center gap-2.5">
                        <img
                            src="/logo-icon.png"
                            alt="Logo DigiKlin"
                            className="h-9 w-auto rounded-lg bg-white"
                        />
                        <div>
                            <p className="font-display text-lg font-bold text-ink">
                                Pesanan Masuk
                            </p>
                            <p className="text-xs text-ink/40">
                                DigiKlin Admin Dashboard
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div
                            data-testid="admin-identity"
                            className="rounded-full border border-border bg-white px-4 py-1.5 text-right"
                        >
                            <p className="text-xs font-bold text-ink">{me.name}</p>
                            <p className="text-[10px] uppercase tracking-wider text-ink/40">
                                {isSuper ? "Superadmin" : "Admin"}
                            </p>
                        </div>
                        <Button
                            data-testid="admin-change-password-button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPwOpen(true)}
                            className="rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            <KeyRound size={15} className="mr-1.5" />
                            Ganti Password
                        </Button>
                        <Button
                            data-testid="admin-change-email-button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEmailOpen(true)}
                            className="rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            <Mail size={15} className="mr-1.5" />
                            Ganti Email
                        </Button>
                        <Button
                            data-testid="admin-refresh-button"
                            variant="outline"
                            size="sm"
                            onClick={load}
                            className="rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            <RefreshCcw size={15} className="mr-1.5" />
                            Segarkan
                        </Button>
                        <Button
                            data-testid="admin-logout-button"
                            variant="outline"
                            size="sm"
                            onClick={onLogout}
                            className="rounded-full border-ink/10 font-bold text-ink hover:bg-red-50 hover:text-red-600"
                        >
                            <LogOut size={15} className="mr-1.5" />
                            Keluar
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-6 py-10">
                <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-200 ${
                                tab === t.id
                                    ? "bg-brand text-white shadow-brand"
                                    : "border border-border bg-white text-ink/60 hover:text-brand"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-8">
                {tab === "orders" && (
                <>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {stats.map((s) => (
                        <div
                            key={s.label}
                            data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
                            className="rounded-2xl border border-border bg-white p-6 shadow-brand"
                        >
                            <p className="text-xs font-bold uppercase tracking-widest text-ink/40">
                                {s.label}
                            </p>
                            <p className="mt-2 font-display text-3xl font-extrabold text-ink">
                                {s.value}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-white shadow-brand">
                    <div className="overflow-x-auto" data-testid="admin-orders-table">
                        <Table className="min-w-[900px]">
                            <TableHeader>
                                <TableRow className="bg-brand-mist/40 hover:bg-brand-mist/40">
                                    <TableHead className="font-bold text-ink/60">Kode</TableHead>
                                    <TableHead className="font-bold text-ink/60">Nama</TableHead>
                                    <TableHead className="font-bold text-ink/60">WhatsApp</TableHead>
                                    <TableHead className="font-bold text-ink/60">Alamat</TableHead>
                                    <TableHead className="font-bold text-ink/60">Jadwal</TableHead>
                                    <TableHead className="font-bold text-ink/60">Titik</TableHead>
                                    <TableHead className="font-bold text-ink/60">Dibuat</TableHead>
                                    <TableHead className="font-bold text-ink/60">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="py-14 text-center text-sm text-ink/50">
                                            Memuat pesanan...
                                        </TableCell>
                                    </TableRow>
                                ) : orders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="py-14 text-center" data-testid="admin-empty-state">
                                            <p className="font-display text-lg font-bold text-ink">
                                                Belum ada pesanan masuk
                                            </p>
                                            <p className="mt-1 text-sm text-ink/50">
                                                Permintaan antar jemput dari
                                                website akan muncul di sini.
                                            </p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    orders.map((o) => (
                                        <TableRow key={o.id} data-testid={`admin-order-row-${o.order_code}`}>
                                            <TableCell className="font-display font-bold text-brand">
                                                {o.order_code}
                                            </TableCell>
                                            <TableCell className="font-semibold text-ink">{o.name}</TableCell>
                                            <TableCell>
                                                <a
                                                    href={`tel:${o.phone}`}
                                                    data-testid={`admin-order-phone-${o.order_code}`}
                                                    className="font-medium text-brand hover:underline"
                                                >
                                                    {o.phone}
                                                </a>
                                            </TableCell>
                                            <TableCell
                                                className="max-w-[220px] truncate text-ink/70"
                                                title={o.address}
                                            >
                                                {o.address}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap text-ink/70">
                                                {o.preferred_date || "—"}
                                                {o.pickup_time ? ` · ${o.pickup_time}` : ""}
                                            </TableCell>
                                            <TableCell>
                                                {o.pickup_lat != null && o.pickup_lng != null ? (
                                                    <a
                                                        href={mapsPointLink(o.pickup_lat, o.pickup_lng)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`admin-order-maps-${o.order_code}`}
                                                        className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold text-brand hover:underline"
                                                    >
                                                        <MapPin size={12} />
                                                        Buka
                                                    </a>
                                                ) : (
                                                    <span className="text-ink/30">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap text-ink/50">
                                                {fmtDate(o.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${STATUS_STYLES[o.status] || "bg-slate-100 text-slate-600"}`}
                                                        data-testid={`admin-order-status-${o.order_code}`}
                                                    >
                                                        {o.status}
                                                    </span>
                                                    <Select
                                                        value={o.status}
                                                        onValueChange={(v) =>
                                                            updateStatus(o.id, v)
                                                        }
                                                    >
                                                        <SelectTrigger
                                                            data-testid={`admin-status-select-${o.order_code}`}
                                                            className="h-8 w-9 justify-center rounded-lg border-ink/10 px-2"
                                                            aria-label="Ubah status pesanan"
                                                        >
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {STATUSES.map((s) => (
                                                                <SelectItem key={s} value={s}>
                                                                    {s}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                </>
                )}

                {tab === "testimonials" && <TestimonialsPanel toast={toast} />}
                {tab === "banners" && <BannerPanel toast={toast} />}
                {tab === "pricing" && isSuper && <PricingPanel toast={toast} />}
                {tab === "admins" && isSuper && (
                    <AdminsPanel me={me} toast={toast} onAuthUpdate={onAuthUpdate} />
                )}
                </div>
            </main>

            <PasswordDialog me={me} open={pwOpen} onOpenChange={setPwOpen} toast={toast} />
            <EmailDialog
                me={me}
                open={emailOpen}
                onOpenChange={setEmailOpen}
                onAuthUpdate={onAuthUpdate}
                toast={toast}
            />
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() =>
        sessionStorage.getItem("dk_admin_token")
    );
    const [me, setMe] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem("dk_admin_info") || "null");
        } catch {
            return null;
        }
    });

    const onLogin = (t, adminInfo) => {
        sessionStorage.setItem("dk_admin_token", t);
        sessionStorage.setItem("dk_admin_info", JSON.stringify(adminInfo));
        setToken(t);
        setMe(adminInfo);
    };

    const onLogout = () => {
        sessionStorage.removeItem("dk_admin_token");
        sessionStorage.removeItem("dk_admin_info");
        setToken(null);
        setMe(null);
    };

    const onAuthUpdate = (t, adminInfo) => {
        if (t) {
            sessionStorage.setItem("dk_admin_token", t);
            setToken(t);
        }
        if (adminInfo) {
            sessionStorage.setItem("dk_admin_info", JSON.stringify(adminInfo));
            setMe(adminInfo);
        }
    };

    if (token && !me) {
        // Sesi lama tanpa info admin — tarik profil dari /admin/me
        api.get("/admin/me")
            .then((res) => {
                sessionStorage.setItem("dk_admin_info", JSON.stringify(res.data));
                setMe(res.data);
            })
            .catch(() => onLogout());
        return null;
    }

    return token && me ? (
        <Dashboard me={me} onLogout={onLogout} onAuthUpdate={onAuthUpdate} />
    ) : (
        <LoginScreen onLogin={onLogin} />
    );
}
